import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Landing, LandingWeddings, LandingInteriors } from "./routes/Landing";
import { Login } from "./routes/Login";
import { Layout } from "./components/Layout";

// Route screens are code-split so a visitor only downloads the bundle for the
// page they land on (the admin screens no longer ship to public/blog readers).
const named = <T extends Record<string, unknown>, K extends keyof T>(p: Promise<T>, k: K) =>
  p.then((m) => ({ default: m[k] as unknown as React.ComponentType<any> }));

const PinsList = lazy(() => named(import("./routes/PinsList"), "PinsList"));
const NewPin = lazy(() => named(import("./routes/NewPin"), "NewPin"));
const Settings = lazy(() => named(import("./routes/Settings"), "Settings"));
const Review = lazy(() => named(import("./routes/Review"), "Review"));
const Fifa = lazy(() => named(import("./routes/Fifa"), "Fifa"));
const Clients = lazy(() => named(import("./routes/Clients"), "Clients"));
const WeddingPins = lazy(() => named(import("./routes/WeddingPins"), "WeddingPins"));
const Boards = lazy(() => named(import("./routes/Boards"), "Boards"));
const DailyPins = lazy(() => named(import("./routes/DailyPins"), "DailyPins"));
const Playbook = lazy(() => named(import("./routes/Playbook"), "Playbook"));
const Terms = lazy(() => named(import("./routes/Legal"), "Terms"));
const Privacy = lazy(() => named(import("./routes/Legal"), "Privacy"));
const BlogIndex = lazy(() => named(import("./routes/Blog"), "BlogIndex"));
const Article = lazy(() => named(import("./routes/Blog"), "Article"));
const Articles = lazy(() => named(import("./routes/Articles"), "Articles"));
const Studio = lazy(() => named(import("./routes/Studio"), "Studio"));
const Samples = lazy(() => named(import("./routes/Samples"), "Samples"));
const Upload = lazy(() => named(import("./routes/Upload"), "Upload"));

function Private({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

export function App() {
  // The weddings.* host is the public Sauntera Weddings blog only — it owns the
  // root so article URLs are clean (weddings.sauntera.com/<slug>). The admin
  // dashboard is reached via the pins-sauntera host instead.
  const onWeddingsHost = typeof window !== "undefined" && /(^|\.)weddings\./i.test(window.location.hostname);
  return (
    <Suspense fallback={<div className="loading">Loading…</div>}>
      {onWeddingsHost ? (
        <Routes>
          <Route path="/" element={<BlogIndex />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* /blog aliases kept so existing pin links keep working. */}
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<Article />} />
          {/* Public stationery customizer (SSR landing pages live at /studio & /studio/:style). */}
          <Route path="/studio/create" element={<Studio />} />
          {/* Any other top-level path = an article slug. */}
          <Route path="/:slug" element={<Article />} />
        </Routes>
      ) : (
        <Routes>
          {/* Public business-card landing — root is a compact vertical
              chooser; the full pitch pages live at /weddings & /interiors. */}
          <Route path="/" element={<Landing />} />
          <Route path="/weddings" element={<LandingWeddings />} />
          <Route path="/interiors" element={<LandingInteriors />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Public Sauntera Weddings blog. */}
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<Article />} />
          <Route path="/studio/create" element={<Studio />} />
          <Route path="/review/:token" element={<Review />} />
          {/* Public cold-outreach sample-pin page (logs a "viewed samples" event). */}
          <Route path="/samples/:clientId" element={<Samples />} />
          {/* Public per-client photo upload page (drops photos into the client doc). */}
          <Route path="/upload/:clientId" element={<Upload />} />
          {/* Dashboard moved under /app so the public landing owns "/". */}
          <Route path="/app" element={<Private><PinsList /></Private>} />
          <Route path="/pins/new" element={<Private><NewPin /></Private>} />
          <Route path="/pins/:id" element={<Private><NewPin /></Private>} />
          <Route path="/fifa" element={<Private><Fifa /></Private>} />
          <Route path="/clients" element={<Private><Clients /></Private>} />
          <Route path="/client-pins" element={<Private><PinsList mode="client" /></Private>} />
          <Route path="/wedding-pins" element={<Private><PinsList mode="wedding" /></Private>} />
          {/* Admin wedding-pin composer — moved off /weddings when the public
              vertical page took that path. */}
          <Route path="/wedding-studio" element={<Private><WeddingPins /></Private>} />
          <Route path="/wedding-studio/:id" element={<Private><WeddingPins /></Private>} />
          <Route path="/boards" element={<Private><Boards /></Private>} />
          <Route path="/daily" element={<Private><DailyPins /></Private>} />
          <Route path="/articles" element={<Private><Articles /></Private>} />
          <Route path="/playbook" element={<Private><Playbook /></Private>} />
          <Route path="/settings" element={<Private><Settings /></Private>} />
          {/* Unknown path → landing (public, safe for both logged-in & anon). */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </Suspense>
  );
}
